import { HonchInstance, HonchConfig } from '../config';
import { Logger } from '../utils/logger';
import { getElementSelector, shouldIgnoreElement } from './utils';

export class FormTracker {
    private honch: HonchInstance;
    private config: HonchConfig;
    private logger: Logger;

    constructor(honch: HonchInstance, config: HonchConfig, logger: Logger) {
        this.honch = honch;
        this.config = config;
        this.logger = logger;
    }

    start(): void {
        if (typeof document === 'undefined') return;

        document.addEventListener('submit', this.handleSubmit, { capture: true });
        this.logger.debug('Form tracker started');
    }

    stop(): void {
        if (typeof document === 'undefined') return;

        document.removeEventListener('submit', this.handleSubmit, { capture: true });
        this.logger.debug('Form tracker stopped');
    }

    private handleSubmit = (event: SubmitEvent): void => {
        const form = event.target as HTMLFormElement;
        if (!form || form.tagName.toLowerCase() !== 'form') return;

        // Check if form should be ignored
        if (shouldIgnoreElement(form, this.config.exclude_elements)) {
            return;
        }

        const properties = this.getFormProperties(form);

        this.honch.capture('$form_submit', properties);

        this.logger.debug('Form submit captured', properties);
    };

    private getFormProperties(form: HTMLFormElement): Record<string, unknown> {
        const fields = Array.from(form.elements)
            .filter((el): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement => {
                const tagName = el.tagName.toLowerCase();
                return tagName === 'input' || tagName === 'select' || tagName === 'textarea';
            })
            .map((el) => ({
                name: el.name || undefined,
                type: (el as HTMLInputElement).type || el.tagName.toLowerCase(),
                // Never capture actual values for privacy
            }))
            .filter((f) => f.name);

        return {
            $element_tag: 'form',
            $element_selector: getElementSelector(form),
            $element_id: form.id || undefined,
            $element_name: form.name || undefined,
            $element_action: form.action || undefined,
            $element_method: form.method || undefined,
            $form_fields: fields,
            $form_field_count: fields.length,
        };
    }
}
